import { get } from "svelte/store";
import { viewport } from "@stores/global";
import { browser } from "@utils/support";

/**
 * Returns correct source for an experience
 *
 * @param {object} options
 * @param {object} options.experience Experience configuration
 * @param {object} options.video Collection of sources for video
 * @param {string} options.params Serialized querystring parameters
 * @returns {string} source
 */
export default function getVideoSourceWithParams({
  experience,
  video,
  params = ""
} = {}) {
  const src = getVideoSource({ experience, video });

  if (!params) {
    return src;
  }

  const url = new URL(src);

  if (url.searchParams.toString().length) {
    return `${url.href}&${params}`;
  }

  return `${url.href}?${params}`;
}

/**
 * Returns correct source for an experience
 *
 * @param {object} options
 * @param {object} options.experience Experience configuration
 * @param {object} options.video Collection of sources for video
 * @returns {string} source
 */
export function getVideoSource({ experience, video } = {}) {
  // Legacy support
  if (video.source) {
    return video.source;
  }

  if (experience.videoFormat === "hls") {
    return video.hls;
  }

  // If we're not a browser, default to medium resolution mp4
  // as the likelihood is HLS won't be natively supported
  if (!browser) {
    return getDefaultVideo(video);
  }

  return getViewportRelativeResolutionSource(video);
}

/**
 * Returns a default source from a list of possible resolutions
 *
 * @param {object} video Video source list
 * @returns {string} Chosen source
 */
function getDefaultVideo(video = {}) {
  const clone = Object.assign({}, video);
  if (clone.hls) {
    delete clone.hls;
  }

  const resolutions = ["360p", "540p", "720p", "1080p"];

  for (let i = 0; i < resolutions.length; i++) {
    if (video[resolutions[i]]) {
      return video[resolutions[i]];
    }
  }

  return Object.values(clone)[0];
}

/**
 * Get correct video source for device width
 *
 * @param {object} video Collection of video sources
 * @returns {string} source
 */
function getViewportRelativeResolutionSource(video) {
  const availableVideoHeights = Object.keys(video).reduce((heights, height) => {
    if (/\d+/.test(height)) {
      heights.push(parseInt(height));
    }
    return heights;
  }, []);

  const windowHeight = Math.max(
    document.documentElement.clientHeight || 0,
    window.innerHeight || 0,
    get(viewport).height || 0
  );

  // Smallest matching video size, or largest video, if none match
  const videoHeight = Math.min(
    Math.min(
      ...availableVideoHeights.filter((height) => height >= windowHeight)
    ),
    Math.max(...availableVideoHeights)
  );

  return video[`${videoHeight}p`];
}
